import { withAuthorizationVerifier } from "../../../components/authorization-verifier";
import { defaultCrudMessages } from "../../shared/crud/simple-crud";
import React, { useCallback, useMemo, useState } from "react";
import { useProperty } from "../../../components/property-provider";
import { useUserAuthInfo } from "../../../lib/auth";
import { CustomModal } from "../../../components/modal";
import { BookingForm } from "../bookingForm";
import { ScenarioEntity } from "./domain/scenario.entity";
import { ScenarioGrpcRepository } from "./infrastructure/repositories/ScenarioGrpcRepository";
import { useScenarios } from "./application/hooks/useScenarios";
import { useScenarioMutations } from "./application/hooks/useScenarioMutations";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import CrudList, { TableColumns } from "../../shared/crud/list";
import { StringRenderer } from "../../shared/renderers/table";
import { Link, useHistory } from "react-router-dom";
import { SortableHandle } from "react-sortable-hoc";
import {
  DefaultTableRowActionsProps,
  Delete,
  Edit,
  StyledReactTooltip,
} from "../../shared/actions/tableActions";
import { Table } from "@devexpress/dx-react-grid-bootstrap4";
import { StyledSwapArrow } from "../../displayManager/styles";
import { faCopy, faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "react-bootstrap";
import SwitchComponent from "../../witadmin/CRUD/Common/SwitchGroup";
import { SwitchWrapper } from "../../taxation";
import { IPropertyChainData, useChainData } from "../../../components/chain-data-provider";
import { messages as scenarioMessages } from "./scenario.messages";
import {
  createActionsSummaryRenderer,
  createConditionsSummaryRenderer,
} from "../shared/components/summaryRenderers";
import { useConfig } from "../../../lib/config";

const scenarioRepository = new ScenarioGrpcRepository();

const messages = {
  ...defaultCrudMessages,
  ...scenarioMessages,
  entityName: {
    id: "orchestrator.scenario.entityName",
    defaultMessage: "Scenarios",
  },
  duplicate: {
    id: "orchestrator.scenario.action.duplicate",
    defaultMessage: "Duplicate",
  },
  bookingForm: {
    id: "menu.orchestrator.bookingForm",
    defaultMessage: "Booking form",
  },
  close: {
    id: "app.close",
    defaultMessage: "Close",
  },
  preview: {
    id: "orchestrator.scenario.action.preview",
    defaultMessage: "Preview",
  },
  edit: {
    id: "app.entityForm.edit",
    defaultMessage: "Edit",
  },
  tableHeaders: defineMessages({
    active: {
      id: "orchestrator.scenario.header.active",
      defaultMessage: "Active",
    },
    ticker: {
      id: "orchestrator.scenario.header.ticker",
      defaultMessage: "Ticker",
    },
    name: {
      id: "orchestrator.scenario.header.name",
      defaultMessage: "Name",
    },
    criteria: {
      id: "orchestrator.scenario.header.criteria",
      defaultMessage: "Conditions (IF)",
    },
    engineMultiConfigId: {
      id: "orchestrator.scenario.header.engineMultiConfigId",
      defaultMessage: "Configuration",
    },
    globalActions: {
      id: "orchestrator.scenario.header.globalActions",
      defaultMessage: "Actions",
    },
  }),
};

function BookingFormAction() {
  const intl = useIntl();
  const userAuthInfo = useUserAuthInfo();
  const [isBookingFormOpen, setBookingFormOpen] = useState(false);

  if (!userAuthInfo?.isWitAdmin) {
    return null;
  }

  return (
    <>
      <Button variant="primary" type="button" onClick={() => setBookingFormOpen(true)}>
        <FormattedMessage {...messages.bookingForm} />
      </Button>
      <CustomModal
        isOpen={isBookingFormOpen}
        onClose={() => setBookingFormOpen(false)}
        title={intl.formatMessage(messages.bookingForm)}
        size="lg">
        <div className="p-4">
          <BookingForm
            embedded
            secondaryAction={
              <Button
                className="px-4 py-2"
                variant="secondary"
                type="button"
                onClick={() => setBookingFormOpen(false)}>
                <FormattedMessage {...messages.close} />
              </Button>
            }
          />
        </div>
      </CustomModal>
    </>
  );
}

function TableActions() {
  return (
    <div className="flex gap-4">
      <Link className="btn btn-primary" to="scenarios/new">
        <FormattedMessage {...messages.create} />
      </Link>
      <BookingFormAction />
    </div>
  );
}

function Scenario() {
  const intl = useIntl();
  const history = useHistory();
  const propertyInfo = useProperty();
  const config = useConfig();
  const { refetch } = useScenarios(propertyInfo.ticker, scenarioRepository, { enabled: false });
  const { deleteScenario, duplicateScenario, reorderScenario } = useScenarioMutations(
    propertyInfo.ticker,
    scenarioRepository
  );
  const chainData: IPropertyChainData | undefined | null = useChainData();

  const tableColumns: TableColumns<ScenarioEntity> = useMemo(
    () => ({
      active: {
        renderer: props => (
          <SwitchWrapper>
            <SwitchComponent
              value={props.value ?? false}
              field={{ className: "witadmin-bs3" }}
              caller=""
              entity={{}}
            />
          </SwitchWrapper>
        ),
        width: 100,
      },
      ticker: {
        renderer: StringRenderer,
        width: 200,
      },
      name: {
        renderer: StringRenderer,
        width: 200,
      },
      criteria: {
        renderer: createConditionsSummaryRenderer(intl, messages),
      },
      engineMultiConfigId: {
        renderer: StringRenderer,
        width: 150,
      },
      globalActions: {
        renderer: createActionsSummaryRenderer(intl, messages),
      },
    }),
    [intl]
  );

  const handleDuplicate = useCallback(
    async (entity: ScenarioEntity, onReloadList: () => void) => {
      await duplicateScenario(entity.id);
      onReloadList();
    },
    [duplicateScenario]
  );

  const handleGetAll = useCallback(async () => {
    const result = await refetch();
    return result.data ?? [];
  }, [refetch]);

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteScenario(id);
    },
    [deleteScenario]
  );

  const handleReorder = useCallback(
    async (id: string, afterId: string | undefined) => {
      await reorderScenario({ id, after: afterId ?? "" });
    },
    [reorderScenario]
  );

  const handleOnGoToEdit = useCallback((id: string) => history.push(`scenarios/edit/${id}`), [
    history,
  ]);

  const handlePreview = useCallback(
    (entity: ScenarioEntity) => {
      const url = `${config.engineBaseUrl}/${chainData?.isChain ? "chain" : "hotel"}/${
        propertyInfo.ticker
      }?appliedScenario=${entity.ticker}&previewMode=1`;
      window.open(url, "_blank");
    },
    [chainData?.isChain, propertyInfo.ticker, config.engineBaseUrl]
  );

  const DragHandle = useMemo(
    () =>
      SortableHandle(() => (
        <Button
          data-tip="React-tooltip"
          data-for="drag"
          size="sm"
          variant="light"
          className="mr-1"
          style={{ cursor: "move" }}>
          <StyledSwapArrow />
        </Button>
      )),
    []
  );

  const RowActions = useMemo(
    () => ({ row, rest, onReloadList }: DefaultTableRowActionsProps<ScenarioEntity>) => {
      return (
        <Table.Cell {...rest} style={{ zIndex: 999 }}>
          <StyledReactTooltip id="drag" effect="solid">
            <FormattedMessage {...messages.drag} />
          </StyledReactTooltip>
          <StyledReactTooltip id="preview" effect="solid">
            <FormattedMessage {...messages.preview} />
          </StyledReactTooltip>
          <StyledReactTooltip id="duplicate" effect="solid">
            <FormattedMessage {...messages.duplicate} />
          </StyledReactTooltip>
          <StyledReactTooltip id="edit" effect="solid">
            <FormattedMessage {...messages.edit} />
          </StyledReactTooltip>

          <DragHandle />

          <Button
            data-tip="React-tooltip"
            data-for="preview"
            size="sm"
            variant="light"
            onClick={() => handlePreview(row)}
            className="mr-1">
            <FontAwesomeIcon icon={faEye} />
          </Button>

          <Button
            data-tip="React-tooltip"
            data-for="duplicate"
            size="sm"
            variant="light"
            onClick={() => handleDuplicate(row, onReloadList)}
            className="mr-1">
            <FontAwesomeIcon icon={faCopy} />
          </Button>

          <Edit row={row} onGoToEdit={handleOnGoToEdit} />

          <Delete
            rest={rest}
            row={row}
            onDelete={handleDelete}
            onReloadList={onReloadList}
            useOverlay
          />
        </Table.Cell>
      );
    },
    [DragHandle, handlePreview, handleDuplicate, handleOnGoToEdit, handleDelete]
  );

  return (
    <CrudList<ScenarioEntity>
      columns={tableColumns}
      onGetAll={handleGetAll}
      onReorder={handleReorder}
      actions={TableActions}
      rowActions={RowActions}
      forceLoad
      messages={messages}
      hideExportButton
    />
  );
}

export default withAuthorizationVerifier(Scenario);
